/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import { useIntl } from 'react-intl'
import { TimeRangeItem, NORMALIZE_FOR_ACCENT } from '../../util/Constants'
import classNames from 'classnames'

export interface TimeRange {
  timeRangeItem: TimeRangeItem
  from: Date
  to: Date
}

interface Props {
  loading: boolean
  timeRangeSelected: TimeRange
  timeRangeItemList: TimeRangeItem[]
  timeRangeOnClickHandler?: (selection: TimeRange) => void
}

const pad2 = (value: number) => String(value).padStart(2, '0')

const createUtcDate = (year: number, monthIndex: number, day: number): Date =>
  new Date(Date.UTC(year, monthIndex, day))

const formatDateOnly = (date: Date): string => {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`
}

const getDayBounds = (date: Date): {start: Date; end: Date} => {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()
  return {
    start: new Date(Date.UTC(year, month, day, 0, 0, 0, 0)),
    end: new Date(Date.UTC(year, month, day, 23, 59, 59, 999)),
  }
}

export const DateRangeSelector: FC<Props> = ({
  loading,
  timeRangeSelected,
  timeRangeItemList,
  timeRangeOnClickHandler,
}) => {
  const intl = useIntl()
  const customTimeRange: TimeRangeItem = {
    code: '-',
    name: 'CUSTOM',
    metric: 'CUSTOM',
    value: 0,
  }
  const [defaultTimeRangeSelected, setDefaultTimeRangeSelected] = useState<TimeRangeItem>()
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [fromInternal, setFromInternal] = useState<Date | null>(null)
  const [toInternal, setToInternal] = useState<Date | null>(null)
  const [fromError, setFromError] = useState<string>('')
  const [toError, setToError] = useState<string>('')
  const [timeRangeListFiltered, setTimeRangeListFiltered] = useState<TimeRangeItem[]>(timeRangeItemList)

  useEffect(() => {
    if (timeRangeSelected) {
      const {start: normalizedFrom} = getDayBounds(timeRangeSelected.from)
      const {end: normalizedTo} = getDayBounds(timeRangeSelected.to)
      setDefaultTimeRangeSelected(timeRangeSelected.timeRangeItem)
      setFromDate(formatDateOnly(normalizedFrom))
      setToDate(formatDateOnly(normalizedTo))
      setFromInternal(normalizedFrom)
      setToInternal(normalizedTo)
    }
  }, [timeRangeSelected])

  const handleQuickSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = NORMALIZE_FOR_ACCENT(e.target.value.trim())
    setTimeRangeListFiltered(
      query
        ? timeRangeItemList.filter((r) => NORMALIZE_FOR_ACCENT(r.name).includes(query))
        : timeRangeItemList
    )
  }

  const evaluateTimeRange = (timeRange: TimeRangeItem) => {
    if (timeRange.metric !== 'CUSTOM') {
      const now = new Date()
      const nowYear = now.getUTCFullYear()
      const nowMonth = now.getUTCMonth()
      const nowDay = now.getUTCDate()
      let fromDateSelected: Date
      let toDateSelected: Date

      switch (timeRange.metric) {
        case 'H': {
          const fromByHour = new Date(now.getTime() - timeRange.value * 60 * 60 * 1000)
          fromDateSelected = createUtcDate(
            fromByHour.getUTCFullYear(),
            fromByHour.getUTCMonth(),
            fromByHour.getUTCDate()
          )
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
        }
        case 'D': {
          const fromByDay = new Date(now.getTime() - timeRange.value * 24 * 60 * 60 * 1000)
          fromDateSelected = createUtcDate(
            fromByDay.getUTCFullYear(),
            fromByDay.getUTCMonth(),
            fromByDay.getUTCDate()
          )
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
        }
        case 'M':
          fromDateSelected = createUtcDate(nowYear, nowMonth - timeRange.value, nowDay)
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
        case 'Y':
          fromDateSelected = createUtcDate(nowYear - timeRange.value, nowMonth, nowDay)
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
        case 'TODAY':
          fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
        case 'YESTERDAY':
          fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay - 1)
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay - 1)
          break
        case 'WEEK': {
          const currentDay = now.getUTCDay()
          const daysSinceMonday = currentDay === 0 ? 6 : currentDay - 1
          fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay - daysSinceMonday)
          toDateSelected = createUtcDate(
            fromDateSelected.getUTCFullYear(),
            fromDateSelected.getUTCMonth(),
            fromDateSelected.getUTCDate() + 6
          )
          break
        }
        case 'LAST_WEEK': {
          const lastWeekCurrentDay = now.getUTCDay()
          const lastWeekDaysSinceMonday = lastWeekCurrentDay === 0 ? 6 : lastWeekCurrentDay - 1
          const currentWeekMonday = createUtcDate(
            nowYear,
            nowMonth,
            nowDay - lastWeekDaysSinceMonday
          )
          fromDateSelected = createUtcDate(
            currentWeekMonday.getUTCFullYear(),
            currentWeekMonday.getUTCMonth(),
            currentWeekMonday.getUTCDate() - 7
          )
          toDateSelected = createUtcDate(
            fromDateSelected.getUTCFullYear(),
            fromDateSelected.getUTCMonth(),
            fromDateSelected.getUTCDate() + 6
          )
          break
        }
        case 'MONTH':
          fromDateSelected = createUtcDate(nowYear, nowMonth, 1)
          toDateSelected = createUtcDate(nowYear, nowMonth + 1, 0)
          break
        default:
          fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
          break
      }      
      const {start: normalizedFrom} = getDayBounds(fromDateSelected)
      const {end: normalizedTo} = getDayBounds(toDateSelected)
      setFromDate(formatDateOnly(normalizedFrom))
      setToDate(formatDateOnly(normalizedTo))
      setFromInternal(normalizedFrom)
      setToInternal(normalizedTo)
      setFromError('')
      setToError('')
    }
    setDefaultTimeRangeSelected(timeRange)
  }

  const parseDateInput = (value: string): Date | null => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) {
      return null
    }

    const [, yearRaw, monthRaw, dayRaw] = match
    const year = Number(yearRaw)
    const month = Number(monthRaw)
    const day = Number(dayRaw)

    const parsed = createUtcDate(year, month - 1, day)

    const isValidDate =
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day

    return isValidDate ? parsed : null
  }

  const fromOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFromDate(value)
    const parsed = parseDateInput(value)
    if (!parsed) {
      setFromInternal(null)
      setFromError('Invalid format. Use YYYY-MM-DD')
      return
    }

    const {start: fromStart} = getDayBounds(parsed)
    setFromInternal(fromStart)

    const toParsed = toDate && parseDateInput(toDate)
    if (toParsed) {
      const {end: toEnd} = getDayBounds(toParsed)
      const error = compareDates('from', fromStart, toEnd)
      if (error) {
        setFromError(error)
      } else {
        setFromError('')
        setToError('')
        setDefaultTimeRangeSelected(customTimeRange)
      }
    } else {
      setFromError('')
    } 
  }

  const toOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setToDate(value)
    const parsed = parseDateInput(value)
    if (!parsed) {
      setToInternal(null)
      setToError('Invalid format. Use YYYY-MM-DD')
      return
    }

    const {end: toEnd} = getDayBounds(parsed)
    setToInternal(toEnd)

    const fromParsed = fromDate && parseDateInput(fromDate)
    if (fromParsed) {
      const {start: fromStart} = getDayBounds(fromParsed)
      const error = compareDates('from', fromStart, toEnd)
      if (error) {
        setToError(error)
      } else {        
        setToError('')
        setFromError('')
        setDefaultTimeRangeSelected(customTimeRange)
      }
    } else {
      setToError('')
    } 
  }

  const compareDates = (input: 'from' | 'to', from: Date, to: Date): string | void => {
    if (!from || !to) {
      return
    } else  if (from.getTime() >= to.getTime()) {
      return input === 'from'
        ? 'From date must be before To date'
        : 'To date must be after From date'
    }     
  }

  const applyOnClickHandler = () => {
    if (fromError || toError) {
      return
    }

    if (fromDate && toDate && !fromError && !toError && defaultTimeRangeSelected && timeRangeOnClickHandler) {
      let from = fromInternal
      let to = toInternal

      if (!from || !to) {
        const parsedFrom = parseDateInput(fromDate)
        const parsedTo = parseDateInput(toDate)
        if (parsedFrom && parsedTo) {
          from = getDayBounds(parsedFrom).start
          to = getDayBounds(parsedTo).end
        }
      }

      if (from && to) {
        from = getDayBounds(from).start
        to = getDayBounds(to).end
        timeRangeOnClickHandler({
          timeRangeItem: defaultTimeRangeSelected,
          from,
          to,
        })
      }
        
    }
  }

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold my-2 fs-6 w-300px p-3'
      data-kt-menu='true'
    >
      <div className='d-flex flex-row'>
        <div className='d-flex flex-column flex-row-fluid'>
          <div className='d-flex flex-row flex-column-fluid'>
            <div className='d-flex flex-row-auto'>
              <div className='d-flex flex-column flex-row-auto'>
                <div className='d-flex flex-column-auto '>
                  <input
                    type='text'
                    className='form-control form-control-sm form-control-solid p-0 ps-3 fs-8 text-dark'
                    placeholder={intl.formatMessage({id: `UTIL.QUICK_SEARCH`})}
                    onChange={handleQuickSearch}
                  />
                </div>

                <div className='card-scroll h-250px py-1 rounded border border-1 border-secondary mt-2'>
                  {timeRangeListFiltered.map((timeRangeItem, index) => (
                    <div
                      key={index}
                      className={classNames('px-2 py-1', {
                        'cursor-pointer bg-hover-secondary':
                          defaultTimeRangeSelected?.code !== timeRangeItem.code,
                        'bg-light-primary': defaultTimeRangeSelected?.code === timeRangeItem.code,
                      })}
                      onClick={() => evaluateTimeRange(timeRangeItem)}
                    >
                      <span className='text-gray-600 fs-8'>{timeRangeItem.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className='d-flex flex-row-fluid ms-2'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-column flex-row-auto px-2'>
                  <label className='form-label fs-8 ms-1'>
                    {intl.formatMessage({id: `UTIL.FROM`})}
                  </label>
                  <>
                    <input
                      type='text'
                      className='form-control form-control-sm form-control-solid px-3 py-0 fs-7 text-dark'
                      placeholder={'YYYY-MM-DD'}
                      onChange={fromOnChangeHandler}
                      value={fromDate}
                      // disabled={disabled}
                    />

                    {fromError && (
                      <div className='d-flex flex-row p-1'>
                        <div className='d-flex flex-column flex-row-fluid'>
                          <div className='d-flex flex-row flex-column-fluid'>
                            <div className='d-flex flex-row-fluid flex-start'>
                              <span className='fs-8 text-danger'>{fromError} </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                </div>
                <div className='d-flex flex-column flex-row-auto p-2'>
                  <label className='form-label fs-8 ms-1'>
                    {intl.formatMessage({id: `UTIL.TO`})}
                  </label>
                  <>
                    <input
                      type='text'
                      className='form-control form-control-sm form-control-solid px-3 py-0 fs-7 text-dark'
                      placeholder={'YYYY-MM-DD'}
                      onChange={toOnChangeHandler}
                      value={toDate}
                      // disabled={disabled}
                    />

                    {toError && (
                      <div className='d-flex flex-row p-1'>
                        <div className='d-flex flex-column flex-row-fluid'>
                          <div className='d-flex flex-row flex-column-fluid'>
                            <div className='d-flex flex-row-fluid flex-start'>
                              <span className='fs-8 text-danger'>{toError} </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                </div>
                <div className='d-flex flex-column flex-row-auto p-2 '>
                  <div className='d-flex flex-row-fluid'>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary w-100'
                      onClick={applyOnClickHandler}
                      data-kt-menu-dismiss='true'
                      disabled={loading || !!fromError || !!toError}
                    >
                      <span className='indicator-label fs-8'>
                        {intl.formatMessage({id: 'UTIL.APPLY'})}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
